import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BookOpen,
  Building2,
  CalendarCheck,
  ChevronDown,
  FileText,
  Home,
  Headset,
  LockKeyhole,
  LogOut,
  MapPinned,
  Network,
  School,
  ScrollText,
  Upload,
  UserCog,
  UsersRound,
} from "lucide-react";
import { DATA_CHANGED_EVENT, request } from "../lib/api";
import { getAdminRole } from "../lib/auth";
import type { FilterData } from "../dashboard/types";
import { ConfirmModal, LanguageSwitcher } from "./ui";
import { APP_VERSION } from "../version";
import { useResizableSidebar } from "../lib/useResizableSidebar";

const groups = [
  {
    label: "Programme",
    items: [
      { label: "Overview", to: "/admin", icon: Home },
      { label: "PD days", to: "/pd-days", icon: BookOpen, count: "pdDays" as const },
      { label: "Content library", to: "/content", icon: FileText, count: "content" as const },
      { label: "Schedule", to: "/schedule", icon: CalendarCheck, count: "schedule" as const },
    ],
  },
  {
    label: "People",
    items: [
      { label: "Teachers", to: "/users", icon: UsersRound, count: "teachers" as const },
      { label: "Facilitators", to: "/facilitators", icon: UserCog, count: "facilitators" as const },
      { label: "Admins", to: "/admins", icon: UserCog, count: "admins" as const },
    ],
  },
  {
    label: "Organisation",
    items: [
      { label: "Districts", to: "/districts", icon: MapPinned, count: "districts" as const },
      { label: "Centres", to: "/centres", icon: Building2, count: "centres" as const },
      { label: "Schools", to: "/schools", icon: School, count: "schools" as const },
      { label: "Cohorts & rooms", to: "/structure", icon: Network, count: "cohortsRooms" as const },
    ],
  },
  {
    label: "Oversight",
    items: [{ label: "Activity log", to: "/activity", icon: ScrollText }],
  },
  {
    label: "Support",
    items: [
      { label: "Help requests", to: "/support", icon: Headset, count: "helpRequests" as const },
    ],
  },
  {
    label: "Research",
    items: [
      {
        label: "Research data",
        to: "/exports",
        icon: FileText,
        count: "responses" as const,
      },
    ],
  },
];

const sectionTitles: Record<string, string> = {
  users: "Teachers and users",
  schools: "Schools",
  centres: "Centres",
  facilitators: "Facilitators",
  admins: "Admin accounts",
  districts: "Districts",
  structure: "Programme structure",
  modules: "Modules",
  content: "Content library",
  "pd-days": "PD days",
  schedule: "Schedule",
  exports: "Research data",
  activity: "Activity log",
  support: "Help requests",
};

const sectionCopy: Record<string, string> = {
  users: "Manage teacher access and participant records",
  schools: "Create schools, assign districts and home centres, and link cohorts",
  centres: "Manage PD delivery centres and their district ownership",
  facilitators: "Create facilitator accounts and manage access status",
  admins: "Manage module admins and super admins",
  districts: "Maintain district records used across schools and centres",
  structure: "Configure cohorts, study groups and rooms",
  modules: "Add content to a module of a PD day",
  content: "Create activities and manage learning media",
  "pd-days": "Build PD days from modules and their content",
  schedule: "Schedule PD days at centres",
  exports: "Browse and download the research records",
  activity: "Review what each admin account has done and attempted",
  support: "Review and respond to teacher and facilitator requests",
};

function UserMenu({ assignedID, onLogout }: { assignedID: string; onLogout: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Open user menu"
        className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-zinc-100"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-zinc-900 text-xs font-semibold text-white">
          {assignedID.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden min-w-0 flex-1 lg:block">
          <strong className="block truncate text-sm font-semibold text-zinc-900">
            {assignedID}
          </strong>
          <span className="block text-xs text-zinc-500">
            {getAdminRole() === "super_admin" ? "Super Admin" : "Admin"} account
          </span>
        </span>
        <ChevronDown size={15} className="hidden text-zinc-400 lg:block" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-40 mb-2 w-56 rounded-xl border border-zinc-200 bg-white p-3 shadow-lg">
          <LanguageSwitcher />
          <div className="my-2 border-t border-zinc-200" />
          <div className="px-2 pb-2 text-xs text-zinc-400">Version {APP_VERSION}</div>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-zinc-600 hover:bg-zinc-100"
            onClick={() => {
              setOpen(false);
              setConfirmLogout(true);
            }}
          >
            <LogOut size={16} />
            {t("signOut")}
          </button>
        </div>
      )}
      <ConfirmModal
        open={confirmLogout}
        title="Sign out?"
        message="You'll need to sign in again to access the admin portal."
        confirmLabel="Sign out"
        onConfirm={onLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}

export function Shell({ assignedID, onLogout }: { assignedID: string; onLogout: () => void }) {
  const isSuperAdmin = getAdminRole() === "super_admin";
  const location = useLocation();
  const isDashboard = location.pathname === "/admin" || location.pathname === "/overview";
  const section = location.pathname.split("/")[1] || "admin";
  const [counts, setCounts] = useState<FilterData["counts"] | null>(null);
  const sidebar = useResizableSidebar();
  useEffect(() => {
    if (!isSuperAdmin) return;
    const loadCounts = () => {
      request<FilterData>("/api/admin/dashboard/filters")
        .then((data) => setCounts(data.counts))
        .catch(() => undefined);
    };
    loadCounts();
    window.addEventListener(DATA_CHANGED_EVENT, loadCounts);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, loadCounts);
  }, [isSuperAdmin, location.pathname]);
  return (
    <div className={`flex min-h-screen w-full bg-zinc-50 ${isDashboard ? "font-dashboard" : ""}`}>
      <aside
        className="sticky top-0 flex h-screen w-16 shrink-0 flex-col border-r border-zinc-200 bg-white lg:w-[var(--sidebar-width)]"
        style={{ "--sidebar-width": `${sidebar.width}px` } as React.CSSProperties}
      >
        {/* The menu's right edge: drag it to make the menu wider or narrower. */}
        <div
          {...sidebar.handleProps}
          className="absolute -right-1 top-0 z-30 hidden h-full w-2 cursor-col-resize touch-none transition-colors hover:bg-interactive/30 focus-visible:bg-interactive/40 focus-visible:outline-none active:bg-interactive/50 lg:block"
        />
        <div className="flex h-16 items-center gap-3 px-3 lg:px-5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-indigo-500 font-semibold text-white">
            C
          </span>
          <span className="hidden lg:block">
            <strong className="block text-sm font-semibold text-zinc-900">CERP PD Days</strong>
            <span className="block text-xs text-zinc-500">Teacher CPD programme</span>
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-2 lg:px-3">
          {groups.map((group) => (
            <div key={group.label} className="mb-5">
              <p className="mb-1 hidden px-2 text-xs font-medium text-zinc-500 lg:block">
                {group.label}
              </p>
              {group.items.map((item) => {
                const Icon = item.icon;
                const count = item.count && counts?.[item.count];
                if (
                  !isSuperAdmin &&
                  item.to !== "/pd-days" &&
                  item.to !== "/content" &&
                  item.to !== "/support"
                ) {
                  return (
                    <div key={item.label} title={`${item.label} requires Super Admin access`}>
                      <button
                        type="button"
                        disabled
                        aria-label={`${item.label} (Super Admin only)`}
                        className="mb-0.5 flex h-10 w-full cursor-not-allowed items-center gap-3 rounded-xl px-3 text-left text-sm text-zinc-400"
                      >
                        <Icon size={18} strokeWidth={1.8} />
                        <span className="hidden min-w-0 flex-1 truncate lg:block">
                          {item.label}
                        </span>
                        <LockKeyhole size={13} className="hidden shrink-0 lg:block" />
                      </button>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    title={item.label}
                    className={({ isActive }) => {
                      const active = isActive;
                      return `mb-0.5 flex h-10 items-center gap-3 rounded-xl px-3 text-sm transition ${active ? "bg-white font-semibold text-zinc-950 ring-1 ring-zinc-200" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"}`;
                    }}
                  >
                    <Icon size={18} strokeWidth={1.8} />
                    <span className="hidden min-w-0 flex-1 truncate lg:block">{item.label}</span>
                    {typeof count === "number" && (
                      <span className="hidden font-mono text-xs text-zinc-500 lg:block">
                        {count.toLocaleString()}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-2 lg:p-3">
          <UserMenu assignedID={assignedID} onLogout={onLogout} />
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        {isDashboard ? (
          <header className="flex h-14 items-center border-b border-zinc-200 bg-white px-4 lg:px-7">
            <h1 className="font-semibold">Overview</h1>
            <span className="ml-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">
              <i className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Cycle in progress
            </span>
            <div className="ml-auto flex gap-2">
              <Link
                to="/users?import=true"
                className="hidden rounded-xl border border-zinc-200 px-4 py-2 text-sm font-medium hover:bg-zinc-50 sm:inline-flex"
              >
                <Upload size={16} className="mr-2" />
                Import teachers
              </Link>
              <Link
                to="/schedule"
                className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
              >
                Schedule PD day
              </Link>
            </div>
          </header>
        ) : null}
        <div
          className={
            isDashboard
              ? "w-full p-4 lg:p-7"
              : "mx-auto w-full max-w-[1680px] px-4 py-7 md:px-8 md:py-9"
          }
        >
          {!isDashboard && (
            <header className="mb-7">
              <h1 className="text-2xl font-bold tracking-tight text-content-primary md:text-3xl">
                {sectionTitles[section] || section.slice(0, 1).toUpperCase() + section.slice(1)}
              </h1>
              <p className="mt-1 text-sm text-content-secondary md:text-base">
                {sectionCopy[section]}
              </p>
            </header>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
